/**
 * Session status and model switching.
 *
 * Wraps the session.status protocol method for:
 * - Getting current session status (model, connection state)
 * - Switching the model for a session
 *
 * Both operations use the same `session.status` method on the wire —
 * including a `model` param triggers a model switch, omitting it
 * returns the current status.
 */

import type { WSClient } from '../ws/client';
import type {
  SessionStatusParams,
  SessionStatusPayload,
} from '../types/protocol';

/**
 * Get the current session status.
 *
 * Sends a `session.status` request without a model parameter,
 * returning the current session configuration.
 *
 * @param client - The WebSocket client instance
 * @param sessionKey - Session key (e.g., "agent:main:main")
 * @returns Current session status including model and connection state
 *
 * @example
 * ```ts
 * const status = await getSessionStatus(client, 'agent:main:main');
 * console.log(`Current model: ${status.model}`);
 * ```
 */
export async function getSessionStatus(
  client: WSClient,
  sessionKey: string,
): Promise<SessionStatusPayload> {
  const params: SessionStatusParams = { sessionKey };
  return client.request<SessionStatusParams, SessionStatusPayload>(
    'session.status',
    params,
  );
}

/**
 * Switch the model for a session.
 *
 * Sends a `session.status` request with a model override.
 * Pass `"default"` to reset to the gateway's default model.
 *
 * @param client - The WebSocket client instance
 * @param sessionKey - Session key (e.g., "agent:main:main")
 * @param model - Model identifier (e.g., "anthropic/claude-opus-4-6") or "default" to reset
 * @returns Updated session status reflecting the model change
 *
 * @example
 * ```ts
 * // Switch to a specific model
 * await switchModel(client, 'agent:main:main', 'anthropic/claude-opus-4-6');
 *
 * // Reset to default model
 * await switchModel(client, 'agent:main:main', 'default');
 * ```
 */
export async function switchModel(
  client: WSClient,
  sessionKey: string,
  model: string,
): Promise<SessionStatusPayload> {
  const params: SessionStatusParams = {
    sessionKey,
    model,
  };
  return client.request<SessionStatusParams, SessionStatusPayload>(
    'session.status',
    params,
  );
}
